// Vindus-system for COP-panelene. Gjør de flytende panelene til ekte «vinduer»:
//  * dra i tittellinja for å flytte (så de ikke bare dekker kartet)
//  * skaler via grep nederst-høyre
//  * klikk → hev til front (z-orden)
//  * Escape → lukk øverste åpne vindu
//  * én-skjerm-modus (nettbrett/laptop): ett vindu om gangen, fullskjerm
// Ingen avhengigheter; jobber på eksisterende paneler uten å skrive dem om.
//
// HVORFOR ESCAPE LIGGER HER, OG IKKE I HVERT PANEL:
// Escape lukket tidligere BARE hjelpe-overlegget (`shortcuts.ts`). Hvert panel
// hadde sin egen `×`, men ingen felles vei ut — så et panel med en `×` som av en
// eller annen grunn ikke traff (dekket, klippet, utenfor viewporten) var i
// praksis umulig å lukke. Det var et rapportert funn på planprosess-panelet.
// WindowManager er det ENESTE stedet som kjenner både z-orden og åpen-tilstand
// for alle vinduer, så her — og bare her — kan «lukk det øverste» bli sant for
// alle paneler samtidig, også de som lages senere.
const SINGLE_KEY = 'skade.singleScreen';
const POS_KEY = 'skade.winpos';

interface Managed { id: string; el: HTMLElement; kategori?: string; lukk?: () => void }

/** Input-typer som ikke er tekstredigering — Escape er trygg oppå dem. */
const IKKE_TEKST_INPUT = ['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color'];

/**
 * Regelen for «skriver brukeren nå?», som REN funksjon av de tre egenskapene som
 * avgjør det. Skilt fra DOM-oppslaget nedenfor slik at den kan testes uten jsdom
 * — testoppsettet i denne appen har ikke DOM, og en regel som ikke kan testes er
 * en regel som stille slutter å gjelde.
 */
export function erSkrivefelt(tagName: string, type?: string, contentEditable = false): boolean {
  if (contentEditable) return true;
  const t = tagName.toUpperCase();
  if (t === 'TEXTAREA' || t === 'SELECT') return true;
  if (t !== 'INPUT') return false;
  return !IKKE_TEKST_INPUT.includes((type ?? 'text').toLowerCase());
}

/**
 * Skriver brukeren i et felt nå? Escape skal ikke rive vekk vinduet under
 * fingrene på den som står midt i en ordre — da mistes ulagret tekst.
 * `contentEditable` er med fordi rike skrivefelt ikke er `<textarea>`.
 */
export function skriverIFelt(el: Element | null): boolean {
  if (!el || typeof (el as HTMLElement).tagName !== 'string') return false;
  const h = el as HTMLElement;
  return erSkrivefelt(h.tagName, (h as HTMLInputElement).type, h.isContentEditable === true);
}

/**
 * Klem en LAGRET vindusposisjon til dagens viewport. Rent uttrykk (testes uten
 * DOM). Grensene er de samme som dra-håndtaket bruker (`makeDraggable`): minst
 * 60 px av vinduet innenfor høyre kant, tittellinja aldri over toppmenyen (48)
 * og aldri under bunnkanten (−40). Dra-en klemmer LIVE mot datidens viewport;
 * uten samme klemming ved GJENOPPRETTING er en posisjon lagret på bred skjerm
 * et vindu som åpner seg usynlig utenfor en smalere — se kommentaren ved
 * gjenopprettingen i `register()`.
 */
export function klemTilViewport(
  p: { left: number; top: number },
  innerWidth: number,
  innerHeight: number,
): { left: number; top: number } {
  return {
    left: Math.max(0, Math.min(innerWidth - 60, p.left)),
    top: Math.max(48, Math.min(innerHeight - 40, p.top)),
  };
}

/**
 * Hvilket vindu ligger øverst av de åpne? Rent uttrykk, slik at rekkefølge-
 * regelen kan testes uten DOM-oppsett. Vinduer uten satt z-index (aldri hevet
 * til front) taper mot ett som er hevet — de har lavest reell stablingsverdi.
 */
export function overste<T extends { open: boolean; z: number }>(vinduer: readonly T[]): T | null {
  let best: T | null = null;
  for (const v of vinduer) {
    if (!v.open) continue;
    if (!best || v.z >= best.z) best = v; // «>=» → siste åpnede vinner ved likhet
  }
  return best;
}

/**
 * Hvilke vinduer skal lukkes når ett åpnes? ÉN regel: vinduer i SAMME kategori
 * er gjensidig utelukkende.
 *
 * BAKGRUNN — DET RAPPORTERTE FUNNET: de flytende kommunikasjonspanelene (Ny
 * stridsmelding, Feltveksler, RFI, Kontakter, PTT) stablet seg oppå hverandre
 * uten noen styring. Operatøren åpnet fire og satt igjen med et kartbilde dekket
 * av halvt synlige paneler som måtte lukkes én for én. Ingenting koblet dem, så
 * ingenting kunne rydde. Kategorien er koblingen: du kan ha kartverktøy og et
 * kommunikasjonspanel åpent samtidig, men aldri to kommunikasjonspaneler.
 *
 * Ren funksjon fordi den ellers bare finnes inne i en MutationObserver, der den
 * er umulig å teste og lett å ødelegge: vinduer UTEN kategori (kartlag,
 * tegnforklaring, planprosess …) skal ALDRI berøres.
 */
export function lukkesVedApning<T extends { id: string; open: boolean; kategori?: string }>(
  vinduer: readonly T[],
  apnetId: string,
  kategori?: string,
): T[] {
  if (!kategori) return [];
  return vinduer.filter((v) => v.id !== apnetId && v.open && v.kategori === kategori);
}

export class WindowManager {
  // Kjente tittellinjer på tvers av panelene (Sentralbord, rapport, chat, intel …).
  static readonly HEADER_SEL =
    '.report-head, .sb-convhead, .sb-railhead, .chat-head, .intel-head, .ptt-head, ' +
    '.thread-head, .checklist-head, .panel-head, [class$="-head"], [class$="-header"]';
  // «Åpne i eget vindu»-ikon (ramme + pil ut).
  static readonly POPOUT_SVG =
    '<svg viewBox="0 0 20 20" width="15" height="15" stroke="currentColor" fill="none" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M11 3h6v6"/><path d="M17 3l-7 7"/><path d="M15 11.5V16a1.5 1.5 0 0 1-1.5 1.5H4.5A1.5 1.5 0 0 1 3 16V6.5A1.5 1.5 0 0 1 4.5 5H9"/></svg>';
  private z = 40;
  private wins: Managed[] = [];
  private single = false;
  private pos: Record<string, { left: number; top: number; width?: number; height?: number }> = {};

  constructor() {
    this.single = localStorage.getItem(SINGLE_KEY) === '1';
    try { this.pos = JSON.parse(localStorage.getItem(POS_KEY) ?? '{}') || {}; } catch { this.pos = {}; }
    document.body.classList.toggle('single-screen', this.single);

    // Escape lukker øverste åpne vindu. `capture: false` og ingen
    // `preventDefault` når ingenting ble lukket — da skal tasten fortsatt nå
    // hjelpe-overlegget og kommandopaletten, som har sin egen Escape.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (skriverIFelt(document.activeElement)) return;
      if (this.lukkOverste()) e.preventDefault();
    });
  }

  isSingle() { return this.single; }

  /**
   * Lukk øverste åpne vindu. Returnerer om noe FAKTISK ble lukket, slik at
   * kalleren kan la tasten gå videre når det ikke var noe å lukke.
   */
  lukkOverste(): boolean {
    const kandidat = overste(
      this.wins.map((w) => ({ w, open: w.el.classList.contains('open'), z: +w.el.style.zIndex || 0 })),
    );
    if (!kandidat) return false;
    // Panelene eier sin egen `open`-tilstand (de fleste har en `toggle()` med et
    // internt flagg). `lukk` bruker panelets egen lukkefunksjon der den finnes og
    // varsler `onLukket` — ellers ville neste klikk på dock-knappen bare satt
    // flagget tilbake til «lukket» og panelet ville krevd TO klikk for å åpnes.
    this.lukk(kandidat.w);
    return true;
  }

  /** Varsles når Escape lukket et vindu, så eiere kan speile tilstanden. */
  onLukket?: (id: string, el: HTMLElement) => void;

  setSingle(on: boolean) {
    this.single = on;
    localStorage.setItem(SINGLE_KEY, on ? '1' : '0');
    document.body.classList.toggle('single-screen', on);
    if (on) {
      // Behold kun det øverste åpne vinduet.
      const open = this.wins.filter((w) => w.el.classList.contains('open'));
      const top = open.sort((a, b) => (+b.el.style.zIndex || 0) - (+a.el.style.zIndex || 0))[0];
      for (const w of open) if (w !== top) w.el.classList.remove('open');
    }
  }

  /** Registrer et panel som et flyttbart vindu. `handle` = drag-håndtak-selektor,
   * `onPopout` = valgfri handler som åpner panelet i eget vindu (gir ⤢-knapp),
   * `opts.kategori` = gruppe der bare ETT vindu er åpent om gangen, og
   * `opts.lukk` = panelets EGEN lukkefunksjon (så dens interne `open`-flagg følger
   * med — uten den ville neste dock-klikk krevd to trykk). */
  register(id: string, el: HTMLElement, handle?: string, onPopout?: () => void, opts?: { kategori?: string; lukk?: () => void }) {
    el.classList.add('wm-window');
    this.wins.push({ id, el, kategori: opts?.kategori, lukk: opts?.lukk });
    const grip = document.createElement('div');
    grip.className = 'wm-resize'; el.append(grip);
    this.makeResizable(el, grip, id);

    // Finn en EKTE tittellinje å dra i — aldri hele panelet (da «hopper»
    // vinduet ved et hvilket som helst klikk+mikrobevegelse). Rekkefølge:
    // eksplisitt selektor → kjente header-klasser → generisk header → første barn.
    const handleEl = (handle ? el.querySelector<HTMLElement>(handle) : null)
      ?? el.querySelector<HTMLElement>(WindowManager.HEADER_SEL)
      ?? el.querySelector<HTMLElement>('header')
      ?? (el.firstElementChild as HTMLElement | null)
      ?? el;
    handleEl.classList.add('wm-handle');
    this.makeDraggable(el, handleEl, id);

    // Standardisert ⤢ pop-out-knapp (til egen skjerm) — samme affordans på hvert
    // vindu som støtter det. Plasseres i tittellinja (venstre for ×).
    if (onPopout && !el.querySelector('.wm-popout')) {
      const pop = document.createElement('button');
      pop.type = 'button'; pop.className = 'wm-popout'; pop.title = 'Åpne i eget vindu (egen skjerm)';
      pop.setAttribute('aria-label', 'Åpne i eget vindu');
      // Tydelig knapp: ikon + etikett (ikke en gjemt liten glyf).
      pop.innerHTML = WindowManager.POPOUT_SVG + '<span class="wm-popout-lbl">Eget vindu</span>';
      pop.addEventListener('pointerdown', (e) => e.stopPropagation());
      pop.addEventListener('click', (e) => { e.stopPropagation(); onPopout(); });
      handleEl.appendChild(pop);
    }

    el.addEventListener('pointerdown', () => this.toFront(el), true);
    // Gjenopprett lagret posisjon. Klemmingen mot dagens viewport skjer IKKE
    // her — panelet er `display: none` nå og kan ikke måles (rect er 0×0), og
    // `style.left` lever i offset-forelderens rom, ikke i viewportets. Se
    // `sikreSynlig()`, som retter på MÅLT posisjon ved hver åpning.
    const p = this.pos[id];
    if (p) { el.style.left = `${p.left}px`; el.style.top = `${p.top}px`; el.style.right = 'auto'; el.style.transform = 'none'; if (p.width) el.style.width = `${p.width}px`; if (p.height) el.style.height = `${p.height}px`; }

    this.observerApning(el, id, opts?.kategori, true);
  }

  /**
   * Registrer et panel KUN for kategori-regelen (og Escape) — ingen dra, skaler
   * eller z-heving.
   *
   * HVORFOR DENNE FINNES: to av kommunikasjonspanelene — «Ny stridsmelding»
   * (`tools/report-composer.ts`) og PTT — henger i `<body>`, ikke i kartkolonnen,
   * og fanges derfor ikke av `WIN_SELECTORS`-sveipen i main. Det ble oppdaget i
   * nettleseren: regelen «ett samband-panel om gangen» virket for Feltveksler ↔
   * Kontakter, men Ny stridsmelding ble stående oppå Feltveksleren fordi den
   * aldri var et registrert vindu. Å gjøre dem til FULLE wm-vinduer ville endret
   * plassering, z-orden og overflow (`.wm-window { overflow: visible }`) på to
   * paneler som ikke ba om det — regelen trenger bare å vite at de finnes.
   */
  registrerKategori(id: string, el: HTMLElement, kategori: string, lukk: () => void) {
    this.wins.push({ id, el, kategori, lukk });
    this.observerApning(el, id, kategori, false);
  }

  /** Én-skjerm: åpning av ett vindu lukker de andre + hev til front. I tillegg
   * gjelder kategori-regelen alltid (også på stor skjerm) — `lukkesVedApning`. */
  private observerApning(el: HTMLElement, id: string, kategori: string | undefined, hevTilFront: boolean) {
    const obs = new MutationObserver(() => {
      if (!el.classList.contains('open')) return;
      if (hevTilFront) { this.toFront(el); this.sikreSynlig(el); }
      if (this.single) { for (const w of this.wins) if (w.el !== el) this.lukk(w); return; }
      for (const w of lukkesVedApning(this.tilstand(), id, kategori)) {
        const m = this.wins.find((x) => x.id === w.id);
        if (m) this.lukk(m);
      }
    });
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
  }

  /**
   * Et vindu som åpnes SKAL kunne ses og gripes.
   *
   * RAPPORTERT SOM: «Utvikling til Strid ser bare ut som den zoomer litt ut,
   * ikke noe vindu popper ut.» Vinduet åpnet seg — utenfor skjermen. Posisjonen
   * lagres ved dra-slutt og gjenopprettes ved neste økt, men dra-klemmingen
   * gjelder DATIDENS viewport: lagret på bred skjerm og lest på smalere (annen
   * maskin, mindre nettleservindu, endret zoom) kan hele vinduet havne utenfor.
   * localStorage overlever både økter og deployer, så panelet var «borte» på
   * ubestemt tid — Escape og nytt dock-klikk slår bare av/på et usynlig vindu.
   * Reprodusert i Chromium: lagret left 2500, viewport 1280 → `open=true`,
   * boks på x=2764, ingenting synlig.
   *
   * Rettes her — ved ÅPNING, på MÅLT posisjon — og ikke ved gjenoppretting i
   * `register()`, av to grunner som begge ble funnet empirisk:
   *   1. Ved registrering er panelet `display: none`; rect er 0×0 og kan ikke
   *      brukes til noe.
   *   2. `style.left` lever i offset-FORELDERENS koordinatrom, mens lagret
   *      posisjon er viewport-koordinater (fra `getBoundingClientRect` ved
   *      dra-slutt). Første forsøk klemte tallet i feil rom: `left` ble 1220,
   *      panelet sto på x=1484 — fortsatt usynlig. Delta mellom målt og ønsket
   *      posisjon er immunt mot forelderens offset og transformer.
   */
  private sikreSynlig(el: HTMLElement) {
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) return; // ikke målbar — ingenting å rette
    // CSS-plasserte paneler (uten inline left/top) står der designet la dem.
    const curL = parseFloat(el.style.left), curT = parseFloat(el.style.top);
    if (Number.isNaN(curL) || Number.isNaN(curT)) return;
    const m = klemTilViewport({ left: r.left, top: r.top }, window.innerWidth, window.innerHeight);
    const dx = m.left - r.left, dy = m.top - r.top;
    if (!dx && !dy) return;
    el.style.left = `${curL + dx}px`;
    el.style.top = `${curT + dy}px`;
  }

  /** Vinduene som en ren liste (id/åpen/kategori) — grunnlaget for regelen. */
  private tilstand() {
    return this.wins.map((w) => ({ id: w.id, open: w.el.classList.contains('open'), kategori: w.kategori }));
  }

  /**
   * Lukk ett vindu og hold panelets EGEN tilstand i takt. Panelene eier et internt
   * `open`-flagg; river vi bare bort CSS-klassen, tror panelet fortsatt at det er
   * åpent og neste klikk på dock-knappen «gjør ingenting» (den bare toggler flagget
   * tilbake). Derfor foretrekkes panelets egen lukkefunksjon, med klasse-fjerning
   * som sikring dersom den ikke gjorde jobben.
   */
  private lukk(w: Managed) {
    if (!w.el.classList.contains('open')) return;
    w.lukk?.();
    if (w.el.classList.contains('open')) w.el.classList.remove('open');
    this.onLukket?.(w.id, w.el);
  }

  private toFront(el: HTMLElement) { el.style.zIndex = String(++this.z); }

  private savePos() {
    try { localStorage.setItem(POS_KEY, JSON.stringify(this.pos)); } catch { /* full */ }
  }

  private makeDraggable(el: HTMLElement, handle: HTMLElement, id: string) {
    // «pending» = pekeren er nede, men vi drar ikke før den har flyttet seg
    // forbi terskelen (DRAG_THRESHOLD px). Slik utløser et rent klikk ALDRI
    // flytting — det var årsaken til at vinduet «hoppet vekk».
    const THRESH = 5;
    let sx = 0, sy = 0, ox = 0, oy = 0, pending = false, active = false, pid = -1;
    handle.addEventListener('pointerdown', (e) => {
      if (this.single) return; // fullskjerm i én-skjerm-modus — ingen flytting
      const t = e.target as HTMLElement;
      if (t.closest('button, input, select, textarea, a, .sb-row, [role="button"]')) return; // ikke dra på kontroller
      pending = true; active = false; pid = e.pointerId;
      const r = el.getBoundingClientRect();
      sx = e.clientX; sy = e.clientY; ox = r.left; oy = r.top;
    });
    handle.addEventListener('pointermove', (e) => {
      if (!pending && !active) return;
      if (!active) {
        if (Math.abs(e.clientX - sx) < THRESH && Math.abs(e.clientY - sy) < THRESH) return;
        // Terskel passert → start ekte flytting. Forankre eksplisitt venstre/topp.
        active = true;
        el.style.left = `${ox}px`; el.style.top = `${oy}px`;
        el.style.right = 'auto'; el.style.bottom = 'auto'; el.style.transform = 'none';
        try { handle.setPointerCapture(pid); } catch { /* ok */ }
      }
      const left = Math.max(0, Math.min(window.innerWidth - 60, ox + (e.clientX - sx)));
      const top = Math.max(48, Math.min(window.innerHeight - 40, oy + (e.clientY - sy)));
      el.style.left = `${left}px`; el.style.top = `${top}px`;
      e.preventDefault();
    });
    const end = () => {
      pending = false;
      if (!active) return; active = false;
      const r = el.getBoundingClientRect();
      this.pos[id] = { ...this.pos[id], left: r.left, top: r.top };
      this.savePos();
    };
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  }

  private makeResizable(el: HTMLElement, grip: HTMLElement, id: string) {
    let sx = 0, sy = 0, ow = 0, oh = 0, active = false;
    grip.addEventListener('pointerdown', (e) => {
      if (this.single) return;
      active = true;
      const r = el.getBoundingClientRect();
      sx = e.clientX; sy = e.clientY; ow = r.width; oh = r.height;
      grip.setPointerCapture(e.pointerId);
      e.preventDefault(); e.stopPropagation();
    });
    grip.addEventListener('pointermove', (e) => {
      if (!active) return;
      const width = Math.max(220, Math.min(window.innerWidth - 40, ow + (e.clientX - sx)));
      const height = Math.max(160, Math.min(window.innerHeight - 60, oh + (e.clientY - sy)));
      el.style.width = `${width}px`; el.style.height = `${height}px`;
    });
    const end = () => {
      if (!active) return; active = false;
      const r = el.getBoundingClientRect();
      this.pos[id] = { ...this.pos[id], left: this.pos[id]?.left ?? r.left, top: this.pos[id]?.top ?? r.top, width: r.width, height: r.height };
      this.savePos();
    };
    grip.addEventListener('pointerup', end);
    grip.addEventListener('pointercancel', end);
  }
}
